import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Tooltip
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import SearchIcon from '@material-ui/icons/Search'
import LinkOffIcon from '@material-ui/icons/LinkOff'
import axios from 'axios'
import VendorProductDetail from './VendorProductDetail'

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  statsRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap'
  },
  statCard: {
    padding: theme.spacing(2),
    textAlign: 'center',
    minWidth: 140,
    flex: 1
  },
  statValue: {
    fontSize: '1.8rem',
    fontWeight: 700
  },
  statLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5
  },
  vendorChips: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap'
  },
  filterRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  filterControl: {
    minWidth: 160
  },
  searchField: {
    minWidth: 260
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  skuCell: {
    fontFamily: 'monospace',
    fontSize: '0.8rem'
  },
  chip: {
    color: '#fff',
    fontWeight: 500,
    fontSize: '0.75rem',
    height: 24
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(6),
    color: theme.palette.text.secondary
  }
}))

const MATCHED_BY_COLORS = {
  manual: '#2196f3',
  suggested: '#ff9800',
  imported: '#9c27b0'
}

function getConfidenceColor(score) {
  if (score >= 0.85) return '#4caf50'
  if (score >= 0.5) return '#ff9800'
  return '#f44336'
}

const MappingDashboard = () => {
  const classes = useStyles()
  const [stats, setStats] = useState(null)
  const [mappings, setMappings] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50)
  const [vendorFilter, setVendorFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [matchedByFilter, setMatchedByFilter] = useState('')
  const [search, setSearch] = useState('')
  const [categories, setCategories] = useState([])
  const [selectedProductId, setSelectedProductId] = useState(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const debounceRef = useRef(null)

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get('/api/vendor-catalogs/mappings/stats')
      setStats(res.data)
    } catch (err) {
      console.error('Failed to load mapping stats:', err)
    }
  }, [])

  const fetchMappings = useCallback(async (currentPage, currentLimit) => {
    setLoading(true)
    try {
      const params = {
        limit: currentLimit,
        skip: currentPage * currentLimit
      }
      if (vendorFilter) params.vendorName = vendorFilter
      if (categoryFilter) params.category = categoryFilter
      if (matchedByFilter) params.matchedBy = matchedByFilter
      if (search) params.search = search

      const res = await axios.get('/api/vendor-catalogs/mappings', { params })
      setMappings(res.data.mappings || [])
      setTotal(res.data.total || 0)
    } catch (err) {
      console.error('Failed to load mappings:', err)
      setMappings([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [vendorFilter, categoryFilter, matchedByFilter, search])

  // Load stats + categories on mount
  useEffect(() => {
    fetchStats()
    axios.get('/api/vendor-catalogs/products/categories')
      .then(res => { if (Array.isArray(res.data)) setCategories(res.data) })
      .catch(() => {})
  }, [fetchStats])

  // Debounced fetch on filter changes
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setPage(0)
      fetchMappings(0, rowsPerPage)
    }, 300)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [vendorFilter, categoryFilter, matchedByFilter, search, rowsPerPage, fetchMappings])

  // Re-fetch on page change (immediate)
  useEffect(() => {
    if (page > 0) fetchMappings(page, rowsPerPage)
  }, [page])

  const handleUnmap = async (e, mapping) => {
    e.stopPropagation()
    try {
      await axios.post(`/api/vendor-catalogs/products/${mapping.vendorProductId}/unmap`)
      fetchMappings(page, rowsPerPage)
      fetchStats()
    } catch (err) {
      console.error('Unmap failed:', err)
    }
  }

  const vendorNames = stats ? stats.vendors.map(v => v.vendorName).sort() : []

  return (
    <div className={classes.root}>
      {/* Coverage Stats Cards */}
      {stats && (
        <>
          <div className={classes.statsRow}>
            <Paper className={classes.statCard}>
              <Typography className={classes.statValue}>{stats.totalAspireItems.toLocaleString()}</Typography>
              <Typography className={classes.statLabel}>Total Aspire Items</Typography>
            </Paper>
            <Paper className={classes.statCard}>
              <Typography className={classes.statValue} style={{ color: '#4caf50' }}>
                {stats.mappedCount.toLocaleString()}
              </Typography>
              <Typography className={classes.statLabel}>Mapped</Typography>
            </Paper>
            <Paper className={classes.statCard}>
              <Typography className={classes.statValue} style={{ color: '#ff9800' }}>
                {stats.unmappedCount.toLocaleString()}
              </Typography>
              <Typography className={classes.statLabel}>Unmapped</Typography>
            </Paper>
            <Paper className={classes.statCard}>
              <Typography className={classes.statValue} style={{ color: '#2196f3' }}>
                {stats.coveragePercent}%
              </Typography>
              <Typography className={classes.statLabel}>Coverage</Typography>
            </Paper>
          </div>

          {/* Per-vendor chips */}
          <div className={classes.vendorChips}>
            {stats.vendors.map(v => (
              <Chip
                key={v.vendorName}
                label={`${v.vendorName}: ${v.mapped} / ${v.total} (${v.total > 0 ? Math.round((v.mapped / v.total) * 100) : 0}%)`}
                variant="outlined"
                onClick={() => setVendorFilter(vendorFilter === v.vendorName ? '' : v.vendorName)}
                color={vendorFilter === v.vendorName ? 'primary' : 'default'}
              />
            ))}
          </div>
        </>
      )}

      {/* Filter Row */}
      <div className={classes.filterRow}>
        <FormControl variant="outlined" size="small" className={classes.filterControl}>
          <InputLabel>Vendor</InputLabel>
          <Select
            value={vendorFilter}
            onChange={e => setVendorFilter(e.target.value)}
            label="Vendor"
          >
            <MenuItem value="">All Vendors</MenuItem>
            {vendorNames.map(v => (
              <MenuItem key={v} value={v}>{v}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" className={classes.filterControl}>
          <InputLabel>Category</InputLabel>
          <Select
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
            label="Category"
          >
            <MenuItem value="">All Categories</MenuItem>
            {categories.map(c => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" className={classes.filterControl}>
          <InputLabel>Matched By</InputLabel>
          <Select
            value={matchedByFilter}
            onChange={e => setMatchedByFilter(e.target.value)}
            label="Matched By"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="manual">Manual</MenuItem>
            <MenuItem value="suggested">Suggested</MenuItem>
            <MenuItem value="imported">Imported</MenuItem>
          </Select>
        </FormControl>

        <TextField
          className={classes.searchField}
          variant="outlined"
          size="small"
          placeholder="Search by name, SKU, or item code..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />
      </div>

      {/* Table */}
      {loading && mappings.length === 0 ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : mappings.length === 0 ? (
        <div className={classes.emptyState}>
          <Typography variant="body1">No mappings found</Typography>
        </div>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Aspire Item Name</TableCell>
                  <TableCell>Item Code</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Vendor Product</TableCell>
                  <TableCell>Vendor</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell align="center">Page</TableCell>
                  <TableCell align="center">Confidence</TableCell>
                  <TableCell align="center">Matched By</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mappings.map(m => (
                  <TableRow
                    key={m._id}
                    className={classes.clickableRow}
                    onClick={() => {
                      setSelectedProductId(m.vendorProductId)
                      setDrawerOpen(true)
                    }}
                  >
                    <TableCell>{m.aspireItemName || '—'}</TableCell>
                    <TableCell className={classes.skuCell}>{m.aspireItemCode || '—'}</TableCell>
                    <TableCell>{m.aspireCategory || m.category || '—'}</TableCell>
                    <TableCell>{m.vendorProductName || '—'}</TableCell>
                    <TableCell>{m.vendorName || '—'}</TableCell>
                    <TableCell className={classes.skuCell}>{m.vendorSku || '—'}</TableCell>
                    <TableCell align="center">{m.pageNumber ?? '—'}</TableCell>
                    <TableCell align="center">
                      {m.confidenceScore != null ? (
                        <Chip
                          label={`${Math.round(m.confidenceScore * 100)}%`}
                          size="small"
                          className={classes.chip}
                          style={{ backgroundColor: getConfidenceColor(m.confidenceScore) }}
                        />
                      ) : '—'}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={m.matchedBy}
                        size="small"
                        className={classes.chip}
                        style={{ backgroundColor: MATCHED_BY_COLORS[m.matchedBy] || '#9e9e9e' }}
                      />
                    </TableCell>
                    <TableCell align="center" onClick={e => e.stopPropagation()}>
                      <Tooltip title="Unmap">
                        <IconButton size="small" onClick={(e) => handleUnmap(e, m)}>
                          <LinkOffIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onChangePage={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onChangeRowsPerPage={e => {
              setRowsPerPage(parseInt(e.target.value, 10))
              setPage(0)
            }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </Paper>
      )}

      <VendorProductDetail
        productId={selectedProductId}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  )
}

export default MappingDashboard
